import { Model } from './model';
import { Type } from 'class-transformer';
import { indexOf } from '@stuut/common/src/utils';

export type AttachmentDisposition = 'inline' | 'attachment';

export enum FileType {
  AUDIO = 'audio',
  DOCUMENT = 'document',
  GIF = 'gif',
  IMAGE = 'image',
  VIDEO = 'video',
}

export enum FilePurpose {
  CHAT = 'chat',

  EMAIL = 'email',

  INTEGRATION_DATA_SYNC = 'integration_data_sync',

  KNOWLEDGE_BASE = 'knowledge_base',

  LOCKBOX = 'lockbox',
}

export interface PresignedUrl {
  fields: Record<string, string>;

  url: string;
}

export class Attachment extends Model {
  id: string;

  content_id: string | null;

  disposition: AttachmentDisposition;

  file_type: FileType;

  is_verified: boolean;

  mime_type: string;

  name: string;

  purpose: FilePurpose | null;

  size: number | null;

  src: string | null;

  _file: File | null = null;

  is_uploading = false;

  is_uploaded = false;

  failed_to_upload = false;

  failed_to_delete = false;

  get byteSize(): number {
    return this.size ?? this._file?.size ?? 0;
  }

  get canPreview(): boolean {
    const downloadList = [
      'application/pdf',
      '.pdf',
      'text/csv',
      '.csv',
      'application/vnd.ms-excel',
      '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xlsx',
      'image/bmp',
      '.bmp',
      'image/jpeg',
      '.jpeg',
      'image/jpg',
      '.jpg',
      'image/png',
      '.png',
      'image/gif',
      '.gif',
      'image/tiff',
      '.tif',
      '.tiff',
    ];

    return Boolean(downloadList.includes(this.mime_type));
  }
}

// TODO: We should really use mixins
export class ModelWithAttachments extends Model {
  id: string;

  @Type(() => Attachment)
  attachments: Attachment[];

  get attachmentsTotalSize(): number {
    return (this.attachments ?? []).reduce((total, attachment) => total + attachment.byteSize, 0);
  }

  get hasUploadingAttachments(): boolean {
    return (this.attachments ?? []).some((attachment) => attachment.is_uploading);
  }

  addAttachments(newAttachments: Attachment[]): void {
    const existing = this.attachments || [];
    const toAdd = newAttachments.filter((a) => !existing.some((e) => e.id && e.id === a.id));
    this.attachments = [...existing, ...toAdd];
  }

  removeAttachment(attachment: Attachment): void {
    // Match by id for persisted attachments; fall back to reference identity for
    // in-flight ones that failed before a presigned id was assigned.
    const index = indexOf(this.attachments, (att: Attachment) =>
      attachment.id ? att.id === attachment.id : att === attachment,
    );
    if (index !== -1) this.attachments.splice(index, 1);
  }
}
